import React, { useCallback, useEffect, useState } from "react";
import ProductCard from "../../components/ProductCard";
import Loader from "../../components/Loader";
import { api } from "../../app/Api/ApiSlice";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./storesStyles.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { FaQrcode } from "react-icons/fa";

const LIMIT = 20;

const SingleShop = () => {
    const { name } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [storeData, setStoreData] = useState({});
    const [isAdmin, setIsAdmin] = useState(false);
    const [shelves, setShelves] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Fetch store details
    useEffect(() => {
        if (!name) {
            setStoreData({});
            setIsAdmin(false);
            return;
        }

        let cancelled = false;

        const getStore = async () => {
            try {
                const response = await api.get(
                    `/stores/${encodeURIComponent(name)}`
                );

                if (cancelled) return;

                setStoreData(response?.data?.store || {});
                setIsAdmin(Boolean(response?.data?.isAdmin));
            } catch (error) {
                if (cancelled) return;

                if (
                    error?.code === "ERR_CANCELED" ||
                    error?.name === "CanceledError"
                ) {
                    return;
                }

                toast.error(
                    error?.response?.data?.message ||
                        "Error fetching store"
                );

                setStoreData({});
                setIsAdmin(false);
            }
        };

        getStore();

        return () => {
            cancelled = true;
        };
    }, [name]);

    // Reset shelves when switching stores
    useEffect(() => {
        setShelves([]);
        setPage(1);
        setHasMore(true);
    }, [name]);

    // Fetch shelves with pagination
    useEffect(() => {
        if (!name) {
            setShelves([]);
            setHasMore(false);
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const getStoreShelves = async () => {
            setIsLoading(true);

            try {
                const response = await api.get(
                    `/stores/${encodeURIComponent(name)}/shelves`,
                    {
                        params: {
                            page,
                            limit: LIMIT,
                        },
                    }
                );

                if (cancelled) return;

                const newShelves = Array.isArray(
                    response?.data?.shelves
                )
                    ? response.data.shelves
                    : [];

                setShelves((previousShelves) => {
                    if (page === 1) {
                        return newShelves;
                    }

                    // Prevent duplicate shelves when appending pages
                    const existingIds = new Set(
                        previousShelves.map((shelf) => shelf._id)
                    );

                    const uniqueShelves = newShelves.filter(
                        (shelf) => !existingIds.has(shelf._id)
                    );

                    return [...previousShelves, ...uniqueShelves];
                });

                setHasMore(newShelves.length === LIMIT);
            } catch (error) {
                if (cancelled) return;

                if (
                    error?.code === "ERR_CANCELED" ||
                    error?.name === "CanceledError"
                ) {
                    return;
                }

                toast.error(
                    error?.response?.data?.message ||
                        "Error fetching store shelves"
                );

                setHasMore(false);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        getStoreShelves();

        return () => {
            cancelled = true;
        };
    }, [name, page]);

    // Load the next page when scrolling
    const handleLoadMore = useCallback(() => {
        if (isLoading || !hasMore) return;

        setPage((currentPage) => currentPage + 1);
    }, [isLoading, hasMore]);

    // View store QR code
    const handleQRCode = useCallback(() => {
        if (!name) return;

        navigate(`/b/${encodeURIComponent(name)}/qrcode`, {
            state: {
                url: `https://onnize.com/b/${encodeURIComponent(name)}`,
            },
        });
    }, [name, navigate]);

    // Activate store
    const handleActivate = useCallback(() => {
        if (!storeData?.name) {
            toast.error("Store details are not available yet");
            return;
        }

        navigate(
            `/b/manage/${encodeURIComponent(storeData.name)}/plan`,
            {
                state: {
                    details: storeData,
                },
            }
        );
    }, [navigate, storeData]);

    return (
        <div className="page-body store-shelves">
            {/* SHOP HEADER */}
            <div className="shop-header">
                <div className="shop-info-wrapper">
                    <div className="shop-logo flexed-center">
                        {storeData?.logo?.url ? (
                            <img
                                src={storeData.logo.url}
                                alt={`${storeData?.name || "Store"} logo`}
                                className="shop-logo-img"
                            />
                        ) : (
                            <div className="shop-logo-img">
                                {storeData?.name?.charAt(0) || ""}
                            </div>
                        )}
                    </div>

                    <div className="shop-info-details">
                        <h2 className="shop-name">
                            {storeData?.name || ""}
                        </h2>
                    </div>

                    <div className="feed-header-header">
                        <button
                            type="button"
                            onClick={handleQRCode}
                            aria-label="View store QR code"
                            title="View QR code"
                            className="store-qrcode-button"
                        >
                            <FaQrcode />
                        </button>
                    </div>

                    <div className="store-active-status">
                        {storeData?.isActive ? (
                            <p>Active</p>
                        ) : isAdmin ? (
                            <button
                                type="button"
                                onClick={handleActivate}
                            >
                                Activate
                            </button>
                        ) : (
                            <p>Paused</p>
                        )}
                    </div>
                </div>

                <div className="shop-banner flexed-center">
                    {storeData?.banner?.url ? (
                        <img
                            src={storeData.banner.url}
                            alt={`${storeData?.name || "Store"} banner`}
                            className="shop-image-banner"
                        />
                    ) : (
                        <div className="shop-img-banner">
                            <h3 className="shop-img-banner-text">
                                {storeData?.name || ""}
                            </h3>
                        </div>
                    )}
                </div>

                <div className="shop-desc">
                    <p className="shop-description">
                        {storeData?.description ||
                            "No description available"}
                    </p>
                </div>
            </div>

            {/* COUNTRIES */}
            {Array.isArray(storeData?.countries) &&
                storeData.countries.length > 0 && (
                    <div className="store-countries">
                        <h3>Countries</h3>

                        {storeData.countries.map((country, index) => (
                            <p key={`${country}-${index}`}>
                                {country}
                            </p>
                        ))}
                    </div>
                )}

            {/* SHOP BODY */}
            <div className="shop-body">
                <InfiniteScroll
                    dataLength={shelves.length}
                    next={handleLoadMore}
                    hasMore={hasMore}
                    loader={isLoading ? <Loader /> : null}
                    endMessage={
                        shelves.length > 0 ? (
                            <p className="store-end-message">
                                You have reached the end.
                            </p>
                        ) : null
                    }
                >
                    {shelves.length > 0 ? (
                        shelves.map((shelf) => (
                            <div
                                className="products-category"
                                key={shelf._id || shelf.name}
                            >
                                <div className="products-category-info">
                                    <h2 className="category-name">
                                        {shelf.name}
                                    </h2>

                                    {shelf.description && (
                                        <p>{shelf.description}</p>
                                    )}
                                </div>

                                <div className="store-products-list">
                                    {Array.isArray(shelf.products) &&
                                    shelf.products.length > 0 ? (
                                        shelf.products.map((product) => (
                                            <ProductCard
                                                key={product._id}
                                                product={product}
                                            />
                                        ))
                                    ) : (
                                        <p>No products in this shelf</p>
                                    )}
                                </div>

                                <div className="see-more">
                                    <Link
                                        to={`/b/${encodeURIComponent(
                                            name
                                        )}/${encodeURIComponent(
                                            shelf.name
                                        )}`}
                                    >
                                        <button type="button">
                                            See More
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        ))
                    ) : isLoading ? (
                        <Loader />
                    ) : (
                        <p>
                            This business has not added any products
                        </p>
                    )}
                </InfiniteScroll>
            </div>
        </div>
    );
};

export default SingleShop;